'use client'

import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react'
import Link from 'next/link'
import { IconArrowRight, IconBolt, IconCpu, IconHeadset, IconPower, IconReceiptRefund, IconWorld } from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import { productsHref } from '@/lib/catalog/filters'
import { PAGE_CONTAINER, SectionTag } from './meridian'

export type ReadyFacts = {
  regions: number
  gpus: number
  vram: string
  rate: string
  refundDays: number
}

const SEGMENTS = 14
const CELLS = 7

const PARTICLES = [
  { left: '8%', bottom: '10%', size: 'h-1.5 w-1.5', dur: '7s', delay: '0s', accent: false },
  { left: '18%', bottom: '24%', size: 'h-1 w-1', dur: '9s', delay: '-3s', accent: true },
  { left: '31%', bottom: '6%', size: 'h-2 w-2', dur: '8s', delay: '-5s', accent: false },
  { left: '44%', bottom: '18%', size: 'h-1 w-1', dur: '6.5s', delay: '-1s', accent: true },
  { left: '52%', bottom: '8%', size: 'h-1.5 w-1.5', dur: '7.5s', delay: '-4s', accent: false },
  { left: '60%', bottom: '28%', size: 'h-1 w-1', dur: '8.5s', delay: '-2s', accent: true },
  { left: '67%', bottom: '12%', size: 'h-2 w-2', dur: '6s', delay: '-3.5s', accent: false },
  { left: '74%', bottom: '20%', size: 'h-1.5 w-1.5', dur: '9s', delay: '-6s', accent: true },
  { left: '81%', bottom: '6%', size: 'h-1 w-1', dur: '7s', delay: '-1.5s', accent: false },
  { left: '88%', bottom: '30%', size: 'h-1.5 w-1.5', dur: '8s', delay: '-4.5s', accent: true },
  { left: '94%', bottom: '14%', size: 'h-1 w-1', dur: '6.5s', delay: '-2.5s', accent: false },
  { left: '24%', bottom: '40%', size: 'h-1 w-1', dur: '10s', delay: '-7s', accent: false },
]

const vars = (values: Record<string, string | number>) => values as CSSProperties

/**
 * The homepage's final call to action, as a console launch screen. The 3D
 * power unit tilts toward the pointer, powers up while the primary button is
 * hovered or focused, and the launch meter charges as the section scrolls in.
 * Motion is skipped for visitors who ask for reduced motion.
 */
export function ReadyLaunch({ facts, chooseHref = productsHref() }: { facts: ReadyFacts; chooseHref?: string }) {
  const rootRef = useRef<HTMLElement>(null)
  const rigRef = useRef<HTMLDivElement>(null)
  const frame = useRef(0)
  const still = useRef(false)
  const [inView, setInView] = useState(false)
  const [boost, setBoost] = useState(false)
  const [burst, setBurst] = useState(0)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    still.current = media.matches
    const onMedia = () => (still.current = media.matches)
    media.addEventListener('change', onMedia)

    const io = new IntersectionObserver(
      ([entry]) => {
        root.dataset.live = entry.isIntersecting ? 'on' : 'off'
        if (entry.isIntersecting && entry.intersectionRatio > 0.15) setInView(true)
      },
      { threshold: [0, 0.15, 0.4] },
    )
    io.observe(root)
    return () => {
      io.disconnect()
      media.removeEventListener('change', onMedia)
      cancelAnimationFrame(frame.current)
    }
  }, [])

  function tilt(x: number, y: number) {
    cancelAnimationFrame(frame.current)
    frame.current = requestAnimationFrame(() => {
      rigRef.current?.style.setProperty('--tx', `${(x * 22).toFixed(2)}deg`)
      rigRef.current?.style.setProperty('--ty', `${(-y * 10).toFixed(2)}deg`)
      rootRef.current?.style.setProperty('--px', x.toFixed(3))
      rootRef.current?.style.setProperty('--py', y.toFixed(3))
    })
  }

  function onPointerMove(event: PointerEvent<HTMLElement>) {
    if (event.pointerType !== 'mouse' || still.current) return
    const rect = event.currentTarget.getBoundingClientRect()
    tilt((event.clientX - rect.left) / rect.width - 0.5, (event.clientY - rect.top) / rect.height - 0.5)
  }

  function powerUp() {
    setBoost(true)
    setBurst((count) => count + 1)
  }

  const level = boost ? SEGMENTS : inView ? 10 : 0
  const status = boost ? 'Ready to launch' : inView ? 'Awaiting player one' : 'Standby'

  const chips = [
    {
      id: 'regions',
      icon: IconWorld,
      value: String(facts.regions),
      label: 'Regions',
      note: 'You choose where it runs',
      place: 'xl:left-0 xl:top-[11%]',
      side: 'left' as const,
      depth: '22px',
      dur: '7s',
    },
    {
      id: 'gpus',
      icon: IconCpu,
      value: String(facts.gpus),
      label: 'GPU configurations',
      note: facts.vram,
      place: 'xl:right-0 xl:top-[4%]',
      side: 'right' as const,
      depth: '-18px',
      dur: '8s',
    },
    {
      id: 'rate',
      icon: IconBolt,
      value: facts.rate,
      label: 'On-demand from',
      note: 'Per hour, metered per minute',
      place: 'xl:left-[1%] xl:bottom-[17%]',
      side: 'left' as const,
      depth: '-14px',
      dur: '6.5s',
    },
    {
      id: 'refund',
      icon: IconReceiptRefund,
      value: `${facts.refundDays} days`,
      label: 'Refund window',
      note: null,
      place: 'xl:right-0 xl:bottom-[8%]',
      side: 'right' as const,
      depth: '20px',
      dur: '7.5s',
    },
  ]

  return (
    <section
      ref={rootRef}
      id="ready"
      aria-labelledby="ready-title"
      data-in={inView ? 'on' : 'off'}
      data-boost={boost ? 'on' : 'off'}
      onPointerMove={onPointerMove}
      onPointerLeave={() => tilt(0, 0)}
      className="relative isolate overflow-hidden pb-16 pt-20 md:pb-20 md:pt-24"
    >
      {/* Backdrop */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="rdy-environment absolute inset-0" />
        <div className="rdy-pillar absolute bottom-0 left-[58%] top-0 w-24" />
        <div className="rdy-pillar absolute bottom-0 right-[6%] top-[10%] w-16 opacity-70" />
        <div className="rdy-pillar absolute bottom-[20%] left-[4%] top-0 w-14 opacity-60" />
        <div className="rdy-horizon absolute inset-x-0 bottom-[9%] h-px" />
        {PARTICLES.map((particle, index) => (
          <span
            key={index}
            className={cn('rdy-particle absolute rounded-[1px]', particle.size, particle.accent && 'rdy-particle-accent')}
            style={vars({ left: particle.left, bottom: particle.bottom, '--dur': particle.dur, '--delay': particle.delay })}
          />
        ))}
      </div>

      <div className={PAGE_CONTAINER}>
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-4">
          {/* Headline, buttons and launch meter */}
          <div className="relative z-10 lg:col-span-6 xl:col-span-5">
            <div className="rdy-reveal">
              <SectionTag>Final stage · Press start</SectionTag>
            </div>
            <h2
              id="ready-title"
              className="rdy-reveal mt-6 font-heading font-bold uppercase text-foreground"
              style={vars({ '--delay': '80ms' })}
            >
              <span className="block text-[clamp(3.5rem,8vw,7rem)] leading-[0.84] tracking-[-0.05em]">Ready</span>
              <span className="block text-[clamp(3.5rem,8vw,7rem)] leading-[0.84] tracking-[-0.05em]">Player</span>
              <span className="rdy-outline block pl-[0.6em] text-[clamp(3.5rem,8vw,7rem)] leading-[0.92] tracking-[-0.04em]">
                One?
              </span>
              <span className="mt-5 flex items-center gap-3 text-[clamp(1.4rem,2.8vw,2.5rem)] leading-none tracking-[-0.02em] text-primary">
                <span aria-hidden className="h-[0.18em] w-10 shrink-0 rounded-full bg-primary shadow-[0_0_12px] shadow-primary/70" />
                Your power starts here.
              </span>
            </h2>

            <p
              className="rdy-reveal mt-6 max-w-lg text-base leading-relaxed text-foreground/80 md:text-lg"
              style={vars({ '--delay': '160ms' })}
            >
              Create an account, add credit and start your first cloud PC. Stop it whenever you are done.
            </p>

            <div
              className="rdy-reveal mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
              style={vars({ '--delay': '240ms' })}
            >
              <Link
                href={chooseHref}
                onMouseEnter={powerUp}
                onMouseLeave={() => setBoost(false)}
                onFocus={powerUp}
                onBlur={() => setBoost(false)}
                className="rdy-cta group relative inline-flex h-16 items-center justify-center gap-3 overflow-hidden whitespace-nowrap rounded-full px-8 font-heading text-lg font-bold uppercase tracking-[0.04em] outline-none focus-visible:ring-4 focus-visible:ring-primary/40"
              >
                <span aria-hidden className="rdy-cta-sheen pointer-events-none absolute inset-0" />
                <IconPower aria-hidden className="relative h-5 w-5" stroke={2.5} />
                <span className="relative">Choose your GPU</span>
                <IconArrowRight aria-hidden className="relative h-5 w-5 transition-transform group-hover:translate-x-1" stroke={2.5} />
              </Link>
              <Link
                href="/help"
                className="rdy-ghost inline-flex h-14 items-center justify-center gap-2 whitespace-nowrap rounded-full px-6 font-heading text-sm font-semibold uppercase tracking-[0.08em] text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <IconHeadset aria-hidden className="h-4 w-4 text-accent" />
                Contact support
              </Link>
            </div>

            {/* Launch meter: charges as the section scrolls in, fills while the main button is hovered */}
            <div
              aria-hidden
              className="rdy-reveal rdy-meter mt-8 max-w-md rounded-2xl p-4"
              style={vars({ '--delay': '320ms' })}
            >
              <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.16em]">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <span className={cn('h-1.5 w-1.5 rounded-[1px]', boost ? 'bg-primary shadow-[0_0_8px] shadow-primary' : 'rdy-blink bg-accent')} />
                  Launch sequence
                </span>
                <span className={boost ? 'text-primary' : 'text-foreground/80'}>{status}</span>
              </div>
              <div className="mt-3 flex gap-1">
                {Array.from({ length: SEGMENTS }, (_, index) => (
                  <span
                    key={index}
                    className={cn('rdy-seg h-3 flex-1 rounded-[2px]', index < level && 'rdy-seg-on')}
                    style={vars({ '--i': index })}
                  />
                ))}
              </div>
            </div>
            <p className="rdy-reveal mt-4 text-sm text-foreground/65" style={vars({ '--delay': '380ms' })}>
              Built for gaming, 3D rendering, video editing, AI and compute workloads.
            </p>
          </div>

          {/* The power unit and its floating facts */}
          <div className="rdy-reveal-stage relative lg:col-span-6 xl:col-span-7" style={vars({ '--delay': '120ms' })}>
            <div aria-hidden className="relative mx-auto h-[27rem] max-w-xl sm:h-[33rem] lg:h-[36rem] lg:max-w-none xl:h-[40rem]">
              <div className="rdy-rays absolute left-1/2 top-[46%] h-[44rem] w-[44rem] -translate-x-1/2 -translate-y-1/2" />
              <div className="rdy-gate absolute left-1/2 top-[46%] h-[34rem] w-[34rem] opacity-50" style={vars({ '--delay': '-2s' })} />
              <div className="rdy-gate rdy-gate-lime absolute left-1/2 top-[46%] h-[25rem] w-[25rem]" style={vars({ '--delay': '-4s' })} />
              <div className="rdy-gate absolute left-1/2 top-[46%] h-[17rem] w-[17rem]" />

              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative flex flex-col items-center">
                  {/* light beam, orbit ring (back half) and pad sit behind the unit */}
                  <div className="rdy-beam absolute -top-16 bottom-6 left-1/2 w-[130%] -translate-x-1/2 opacity-70" />
                  <div className="rdy-orbit rdy-orbit-back absolute left-1/2 top-[52%] h-[21rem] w-[21rem] sm:h-[25rem] sm:w-[25rem]">
                    <div className="rdy-halo-spin relative h-full w-full">
                      <div className="rdy-halo absolute inset-0" />
                      <span className="rdy-halo-dot absolute left-1/2 top-0 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full" />
                    </div>
                  </div>
                  <div className="rdy-pad absolute -bottom-9 left-1/2 h-20 w-[150%] -translate-x-1/2 sm:-bottom-11 sm:h-24">
                    <div className="rdy-pad-ring absolute inset-3 sm:inset-4" />
                  </div>
                  <div className="rdy-glow absolute -bottom-6 left-1/2 h-16 w-[120%] -translate-x-1/2" />
                  {burst > 0 && (
                    <span key={burst} className="rdy-burst absolute left-1/2 top-[96%] h-24 w-[150%]" />
                  )}

                  <div className="rdy-persp relative">
                    <div ref={rigRef} className="rdy-rig">
                      <div className="rdy-tower">
                        <div className="rdy-face-top rdy-top rounded-[0.6rem]" />
                        <div className="rdy-face-side rdy-side rounded-[0.6rem]">
                          <span className="rdy-side-light absolute bottom-[12%] left-1/2 top-[52%] w-1 -translate-x-1/2 rounded-full" />
                        </div>
                        <div className="rdy-face-front rdy-front flex flex-col gap-2.5 overflow-hidden rounded-[1.1rem] p-3 sm:gap-3 sm:p-3.5">
                          <span className="rdy-led absolute bottom-8 left-1 top-8 w-1 rounded-full" />
                          {/* status bar */}
                          <div className="flex items-center gap-1.5 pl-2">
                            <span className="rdy-blink h-1.5 w-1.5 rounded-[1px] bg-primary" />
                            <span className="h-1.5 w-1.5 rounded-[1px] bg-accent" />
                            <span className="h-1.5 w-1.5 rounded-[1px] bg-foreground/25" />
                            <span className="ml-auto font-mono text-[9px] uppercase tracking-[0.2em] text-foreground/60">P1 · Power unit</span>
                          </div>
                          {/* glass window: GPU card, energy core, load cells */}
                          <div className="rdy-glass relative flex-1 overflow-hidden rounded-xl">
                            <div className="rdy-card absolute inset-x-2.5 top-2.5 flex h-[27%] items-center justify-around rounded-lg px-1.5">
                              {[0, 1, 2].map((fan) => (
                                <span key={fan} className="rdy-fan aspect-square h-[74%]" />
                              ))}
                              <span className="rdy-led absolute -bottom-1 left-3 right-3 h-[3px] rounded-full" />
                            </div>
                            <div className="rdy-tube absolute bottom-3 left-1/2 top-[38%] w-7 -translate-x-1/2 rounded-full sm:w-8">
                              <span className="rdy-core absolute inset-1 rounded-full" />
                            </div>
                            {[46, 62, 78].map((top) => (
                              <span
                                key={top}
                                className="rdy-coil absolute left-1/2 h-3 w-14 -translate-x-1/2 sm:w-16"
                                style={{ top: `${top}%` }}
                              />
                            ))}
                            {['left-3', 'right-3'].map((place) => (
                              <span key={place} className={cn('absolute bottom-4 flex w-2 flex-col-reverse gap-1', place)}>
                                {Array.from({ length: CELLS }, (_, index) => (
                                  <span
                                    key={index}
                                    className={cn('rdy-cell h-1.5 w-2 rounded-[1px]', index < (boost ? CELLS : inView ? 4 : 1) && 'rdy-cell-on')}
                                    style={vars({ '--i': index })}
                                  />
                                ))}
                              </span>
                            ))}
                            <span className="rdy-glare absolute inset-0" />
                          </div>
                          {/* vents and power button */}
                          <div className="flex items-center gap-3 pl-2">
                            <span className="rdy-power flex h-8 w-8 shrink-0 items-center justify-center rounded-full sm:h-9 sm:w-9">
                              <IconPower className="h-4 w-4" stroke={2.5} />
                            </span>
                            <span className="rdy-vent h-5 flex-1 rounded-sm opacity-80" />
                          </div>
                          <span className="rdy-sheen pointer-events-none absolute inset-0" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* orbit ring, front half */}
                  <div className="rdy-orbit rdy-orbit-front pointer-events-none absolute left-1/2 top-[52%] h-[21rem] w-[21rem] sm:h-[25rem] sm:w-[25rem]">
                    <div className="rdy-halo-spin relative h-full w-full">
                      <div className="rdy-halo absolute inset-0" />
                      <span className="rdy-halo-dot absolute left-1/2 top-0 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Facts: floating around the unit on large screens, a 2×2 grid below it on small ones */}
            <ul aria-label="Platform facts" className="mt-8 grid grid-cols-2 gap-3 xl:mt-0 xl:block">
              {chips.map((chip) => (
                <li
                  key={chip.id}
                  className={cn('rdy-par xl:absolute xl:w-[12.75rem] 2xl:w-[14rem]', chip.place)}
                  style={vars({ '--depth': chip.depth })}
                >
                  <div className="rdy-float h-full" style={vars({ '--dur': chip.dur, '--delay': `-${chip.dur}` })}>
                    <div className="rdy-chip relative h-full rounded-2xl p-3.5 sm:p-4">
                      <span
                        aria-hidden
                        className={cn(
                          'rdy-wire absolute top-1/2 hidden h-px w-10 xl:block',
                          chip.side === 'left' ? '-right-10' : '-left-10',
                        )}
                        style={vars({ '--dir': chip.side === 'left' ? '90deg' : '270deg' })}
                      />
                      <span
                        aria-hidden
                        className={cn(
                          'rdy-node absolute top-1/2 hidden h-1.5 w-1.5 -translate-y-1/2 rounded-[1px] xl:block',
                          chip.side === 'left' ? '-right-[2.85rem]' : '-left-[2.85rem]',
                        )}
                      />
                      <div className="flex items-center gap-2.5">
                        <span aria-hidden className="rdy-chip-icon flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
                          <chip.icon className="h-4 w-4" />
                        </span>
                        <span className="font-mono text-[10px] uppercase leading-tight tracking-[0.14em] text-muted-foreground">
                          {chip.label}
                        </span>
                      </div>
                      <p className="mt-2.5 font-heading text-2xl font-bold tabular-nums leading-none text-foreground sm:text-3xl">
                        {chip.value}
                      </p>
                      <p className="mt-1.5 text-xs leading-snug text-foreground/70">
                        {chip.note ?? (
                          <>
                            Eligible unused credit, subject to the{' '}
                            <Link
                              href="/terms#refunds"
                              className="rounded-sm text-foreground underline decoration-primary/60 underline-offset-2 outline-none hover:decoration-primary focus-visible:ring-2 focus-visible:ring-primary"
                            >
                              refund policy
                            </Link>
                            .
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}
